/**
 * Перенос `presentation/providers/chat_controller/chat_controller.dart`.
 *
 * Сердце приложения: состояние диалога, запись голоса, распознавание,
 * поток ответа LLM и поток анимации аватара. Класс `ChatValue` вынесен
 * в `chatValue.ts` — в Dart он лежал в этом же файле, но здесь действует
 * потолок в 800 строк.
 *
 * Чем веб отличается от Flutter (подробности — у мест вызова):
 *  - `CancelableCompleter` из `package:async` заменён своим (см. сосед);
 *  - временной папки нет: запись сразу отдаёт `File`, поэтому
 *    `_getRecordDirectory()` и `getTemporaryDirectory()` убраны, а флаг
 *    `preparingDirectory` поднимается на время открытия микрофона;
 *  - `openAppSettings()` из permission_handler недоступен — вместо перехода
 *    в настройки показывается тот же тост про доступ к микрофону;
 *  - `CustomLogger` из flutter_toolkit заменён на `console.debug`.
 */

import { Endpoints } from '@/config/endpoints';
import type { AppLanguage } from '@/domain/models/appLanguage';
import { ChatEvent } from '@/domain/models/chatEvent';
import { llmResponseModelTryFromJson } from '@/domain/models/llmResponseModel';
import type { PersonModel } from '@/domain/models/personModel';
import {
  personWaitingEventTryParse,
  type PersonWaitingEvent,
} from '@/domain/models/personWaitingEvent';
import type { ChatRepository } from '@/domain/repository/chatRepository';
import type { PersonRepository } from '@/domain/repository/personRepository';
import type { L10n } from '@/l10n/types';
import { tryGetL10n } from '@/l10n/useL10n';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import { AudioPlayer } from '@/utils/audio/audioPlayer';
import { AudioRecorder } from '@/utils/audio/audioRecorder';
import { CustomValueNotifier } from '@/utils/customValueNotifier';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureKind, FailureOp } from '@/utils/exceptions/appFailure';
import { Nullable } from '@/utils/nullable';
import { SseConnection } from '@/utils/sse/sseClient';
import { sseEventDataAsMap, type SseEvent } from '@/utils/sse/sseEvent';
import { sseBroken, type SseFailure } from '@/utils/sse/sseFailure';

import { CancelableCompleter } from './cancelableCompleter';
import { ChatDurationController } from './chatDurationController';
import {
  assistantMessageCopyWith,
  chatMessageToJson,
  createAssistantMessage,
  createMyMessage,
  type AssistantMessage,
  type ChatMessage,
} from './chatMessage';
import { ChatValue } from './chatValue';

/**
 * Сколько сырого текста нераспознанного события уходит в журнал.
 * Для опознания формата хватает начала, а весь ответ модели там не нужен.
 */
const UNPARSED_EVENT_LOG_LIMIT = 300;

export type ChatEventListener = (event: ChatEvent) => void;

/** Аргументы конструктора [ChatController] — те же, что в Dart. */
export interface ChatControllerInit {
  readonly initialLanguage: AppLanguage;
  readonly initialPerson: PersonModel;
  readonly onTimeExceed: () => void;
  readonly chatRepository: ChatRepository;
  readonly personRepository: PersonRepository;
}

/** Аргументы `_closePersonAnimation({bool? complete})`. */
interface ClosePersonAnimationOptions {
  readonly complete?: boolean;
}

/** Аргументы `_onPersonAnimationError({AppFailure? failure})`. */
interface PersonAnimationErrorOptions {
  readonly failure?: AppFailure;
}

/** Аргументы `_endRecordCompleter({Object? error})`. */
interface EndRecordCompleterOptions {
  readonly error?: unknown;
}

/**
 * `CustomLogger(owner: runtimeType.toString())` из flutter_toolkit —
 * тот же префикс владельца, вывод в отладочный канал консоли.
 */
function logWithOwner(owner: string, message: string): void {
  console.debug(`[${owner}] ${message}`);
}

/** Владелец записей журнала — `runtimeType.toString()` в Dart. */
const LOGGER_OWNER = 'ChatController';

/**
 * `_subsribeToPersonAnimation({bool test = kDebugMode && false})`.
 * Выражение по умолчанию в Dart всегда давало false — оставлено как есть.
 */
const DEFAULT_ANIMATION_TEST_FLAG = false;

export class ChatController extends CustomValueNotifier<ChatValue> {
  constructor(init: ChatControllerInit) {
    super();
    this.initialPerson = init.initialPerson;
    this.initialLanguage = init.initialLanguage;
    this.onTimeExceed = init.onTimeExceed;
    this.chatRepository = init.chatRepository;
    this.personRepository = init.personRepository;
    // Базовый класс не может построить состояние сам: на момент его
    // конструктора поля наследника ещё не присвоены.
    this.initialize();
  }

  static #counter = 0;

  readonly initialPerson: PersonModel;

  readonly initialLanguage: AppLanguage;

  readonly onTimeExceed: () => void;

  readonly chatRepository: ChatRepository;

  readonly personRepository: PersonRepository;

  get l10n(): L10n | null {
    return tryGetL10n();
  }

  /** Список слушателей пересобирается, а не правится на месте. */
  #listeners: readonly ChatEventListener[] = [];

  readonly #player = new AudioPlayer();

  #recorder: AudioRecorder | null = null;

  /**
   * Живые SSE-соединения. Раньше пакет держал их у себя по строковым id;
   * теперь соединение — объект, и его можно закрыть напрямую.
   */
  #animationConnection: SseConnection | null = null;
  #completionConnection: SseConnection | null = null;

  #recordCompleter: CancelableCompleter | null = null;

  /** `late final ChatDurationController` — создаётся в `initState`. */
  #chatDurationController: ChatDurationController | null = null;

  #personWaitingErrorHandled = false;

  get chatDurationController(): ChatDurationController {
    const controller = this.#chatDurationController;
    if (controller === null) {
      throw new Error('ChatController: initialize() не вызван в конструкторе');
    }
    return controller;
  }

  addEventListener(listener: ChatEventListener): void {
    this.#listeners = [...this.#listeners, listener];
  }

  removeEventListener(listener: ChatEventListener): void {
    this.#listeners = this.#listeners.filter((item) => item !== listener);
  }

  static createConnectionId(): string {
    // `(_counter++).toString()` — в строку уходит значение до увеличения.
    const id = ChatController.#counter;
    ChatController.#counter += 1;
    return String(id);
  }

  protected override initState(): void {
    super.initState();
    this.#chatDurationController = new ChatDurationController({
      onTimeExceed: this.onTimeExceed,
    });
    this.#subscribeToPersonAnimation();
  }

  protected override buildState(): ChatValue {
    return new ChatValue({
      person: this.initialPerson,
      language: this.initialLanguage,
    });
  }

  protected override onStateChanged(previous: ChatValue, current: ChatValue): void {
    if (current.busy) {
      this.chatDurationController.stopTimer();
    } else {
      this.chatDurationController.restartTimer();
    }
    if (current.complete !== previous.complete) {
      this.#postEvent(ChatEvent.complete);
    }
    super.onStateChanged(previous, current);
  }

  override dispose(): void {
    this.#chatDurationController?.dispose();
    this.closeCurrentConnection();
    this.#closePersonAnimation();
    this.#player.dispose();
    this.#recorder?.dispose();
    this.#animationConnection?.close();
    this.#completionConnection?.close();
    super.dispose();
  }

  disableKeyboardMode(): void {
    this.updateValue((old) => old.copyWith({ keyboardMode: false }));
  }

  enableKeyboardMode(): void {
    this.updateValue((old) => old.copyWith({ keyboardMode: true }));
  }

  changeHolding(isHolding: boolean): void {
    let changed = false;
    this.updateValue((old) => {
      changed = isHolding !== old.userHolding;
      return old.copyWith({ userHolding: isHolding });
    });
    if (changed) {
      if (this.value.userHolding) {
        void this.#startRecordAudio();
      } else {
        void this.#stopRecordAudio();
      }
    }
  }

  /**
   * Правит первое (самое новое) сообщение ленты, если это ответ аватара
   * с нужным id. В Dart тут был каскад `old.messages..removeAt(0)`, то есть
   * список состояния менялся на месте; здесь он остаётся нетронутым.
   */
  editAssistantMessage(
    id: number,
    onChange: (old: AssistantMessage) => AssistantMessage,
  ): void {
    this.updateValue((old) => {
      const message = old.messages.length > 0 ? old.messages[0] : null;
      if (message !== null && message.id === id && !message.isMy) {
        const newMessage = onChange(message);
        return old.copyWith({ messages: [newMessage, ...old.messages.slice(1)] });
      }
      return old;
    });
  }

  addMessage(message: ChatMessage): void {
    if (this.value.fetchingResponse) return;
    this.updateValue((old) => old.copyWith({ messages: [message, ...old.messages] }));
  }

  /**
   * Вопрос, набранный с клавиатуры или выбранный из подсказок.
   *
   * Отличие от Dart: там `addMessage` молча ничего не делал, пока идёт
   * ответ (`if (value.fetchingResponse) return;`), а `_startFetchResponse()`
   * вызывался следом в любом случае. Второй заход поэтому не добавлял
   * вопрос, но открывал ЕЩЁ ОДИН поток — с идентификатором пузыря, которого
   * нет в ленте. `editAssistantMessage` такой пузырь не находит, и весь
   * ответ модели уходит в никуда: поток идёт, экран пустой.
   *
   * На планшете это не всплывало — там до повторного вызова не доходило.
   * В киоске доходит: посетитель жмёт «отправить» дважды. Поэтому проверка
   * поднята на уровень выше, к самому входу; намерение то же, что у
   * `addMessage`, просто теперь оно распространяется и на запрос ответа.
   */
  async addTextMessage(message: string): Promise<void> {
    if (this.value.fetchingResponse) return;
    this.addMessage(createMyMessage({ audio: null, message }));
    await this.#startFetchResponse();
  }

  async getTranscription(audioFile: File): Promise<string> {
    try {
      this.updateValue((old) => old.copyWith({ translating: true }));
      return await this.chatRepository.getTranscription({
        audioFile,
        language: this.value.language,
        person: this.value.person,
      });
    } catch (e) {
      throw AppFailure.fromException(FailureOp.transcribe, e, stackTraceOf(e));
    } finally {
      this.updateValue((old) => old.copyWith({ translating: false }));
    }
  }

  /** `DeviceFileSource(file.path)` заменён самим `File`: диска в вебе нет. */
  async playAudio(file: File): Promise<void> {
    try {
      await this.#player.setSource(file);
      await this.#player.resume();
    } catch (e) {
      SnackbarManager.instance.showFailure(AppFailure.device(FailureOp.playback, e));
    }
  }

  async stopPlayingAudio(): Promise<void> {
    try {
      await this.#player.stop();
    } catch (e) {
      SnackbarManager.instance.showFailure(AppFailure.device(FailureOp.playback, e));
    }
  }

  clearChat(): void {
    if (this.value.busy) {
      return;
    }
    this.closeCurrentConnection();
    this.updateValue((old) => old.copyWith({ messages: [] }));
  }

  closeCurrentConnection(): void {
    try {
      this.#endRecordCompleter();
      const connectionId = this.value.connectionId;
      if (connectionId !== null) {
        this.updateValue((old) => {
          const firstAnswer = old.firstAnswer;
          const firstQuestion = old.firstQuestion;
          const needClear =
            firstQuestion === null ||
            firstAnswer === null ||
            firstAnswer.message.trim().length === 0;
          return old.copyWith({
            speeking: false,
            messages: needClear ? [] : undefined,
            connectionId: new Nullable(null),
          });
        });
        this.#sendInterrupt();
        this.#completionConnection?.close();
        this.#completionConnection = null;
      }
    } catch (e) {
      SnackbarManager.instance.showFailure(
        AppFailure.fromException(FailureOp.completion, e, stackTraceOf(e)),
      );
    }
  }

  #notifyEventListeners(event: ChatEvent): void {
    // Копия набора: слушатель может отписаться прямо во время обхода.
    for (const listener of [...this.#listeners]) {
      listener(event);
    }
  }

  #postEvent(event: ChatEvent): void {
    logWithOwner(LOGGER_OWNER, `event: ${event}`);
    this.#notifyEventListeners(event);
  }

  /**
   * Имя метода в Dart написано с опечаткой (`_subsribeToPersonAnimation`);
   * здесь оно исправлено, поведение то же.
   */
  #subscribeToPersonAnimation(test: boolean = DEFAULT_ANIMATION_TEST_FLAG): void {
    const sseId = ChatController.createConnectionId();
    this.setValue(
      this.value.copyWith({
        animationConnectionId: new Nullable(sseId),
        personAnimating: true,
      }),
    );
    this.#animationConnection?.close();
    this.#animationConnection = SseConnection.open({
      url: Endpoints.selectLanguage(this.value.person.personBaseUrl),
      body: {
        // `value.language.param` — значением перечисления служит сам param.
        lang: this.value.language,
        test,
      },
      onEvent: (event) => {
        this.#onPersonAnimationEvent(event);
      },
      onDone: () => {
        if (!this.value.personAnimationComplete) {
          // Поток закрылся, а «готов» так и не пришёл.
          this.#onPersonAnimationError({
            failure: AppFailure.fromSse(FailureOp.personAnimation, sseBroken()),
          });
        }
      },
      onFailure: (failure) => {
        this.#onPersonAnimationError({
          failure: AppFailure.fromSse(FailureOp.personAnimation, failure),
        });
      },
    });
  }

  /** Тело `onEvent` потока анимации. */
  #onPersonAnimationEvent(event: SseEvent): void {
    const json = sseEventDataAsMap(event);
    if (json === null) return;
    if (json['type'] === 'error') {
      // PersonWaitingEvent.error полей не несёт — сохраняем текст ноды,
      // иначе AVA-UNKNOWN не разобрать.
      DiagnosticsLog.instance.stream({
        title: 'select_language: нода прислала error',
        // `json.toString()` в Dart печатал карту целиком; `String(json)`
        // в JS дал бы '[object Object]', поэтому сериализация в JSON.
        details: JSON.stringify(json),
        isError: true,
      });
    }
    const waitingEvent = personWaitingEventTryParse(json);
    if (waitingEvent !== null) {
      this.#onPersonWaitingEvent(waitingEvent);
    }
  }

  #onPersonWaitingEvent(event: PersonWaitingEvent): void {
    switch (event.type) {
      case 'info':
        break;
      case 'error':
        this.#onPersonAnimationError();
        break;
      case 'complete':
        this.#closePersonAnimation({ complete: true });
        break;
    }
  }

  #closePersonAnimation({ complete }: ClosePersonAnimationOptions = {}): void {
    const connectionId = this.value.animationConnectionId;
    if (connectionId !== null) {
      this.#animationConnection?.close();
      this.#animationConnection = null;
      this.setValue(
        this.value.copyWith({
          animationConnectionId: new Nullable(null),
          personAnimating: false,
          personAnimationComplete: complete,
        }),
      );
    }
  }

  #onPersonAnimationError({ failure }: PersonAnimationErrorOptions = {}): void {
    if (this.#personWaitingErrorHandled) return;
    this.#personWaitingErrorHandled = true;
    this.#complete();
    this.#closePersonAnimation();
    SnackbarManager.instance.showFailure(
      failure ??
        new AppFailure({
          op: FailureOp.personAnimation,
          kind: FailureKind.unknown,
        }),
    );
  }

  #complete(): void {
    this.setValue(this.value.copyWith({ complete: true }));
  }

  #sendInterrupt(): void {
    const person = this.value.person;
    // В Dart Future не ожидали — «отправил и забыл». В браузере непойманный
    // отказ промиса шумит в консоли, поэтому он гасится с записью в журнал
    // разработчика: поведение то же, причина не теряется.
    void this.personRepository.sendInterrupt(person).catch((error: unknown) => {
      DiagnosticsLog.instance.event(`interrupt: ${String(error)}`, { isError: true });
    });
  }

  async #startFetchResponse(): Promise<void> {
    const connectionId = ChatController.createConnectionId();
    const message = createAssistantMessage({ message: '' });
    this.addMessage(message);
    this.updateValue((old) => old.copyWith({ connectionId: new Nullable(connectionId) }));
    const messageId = message.id;
    // `value.messages.reversed` — ленивая копия в Dart; `reverse()` в JS
    // меняет массив на месте, поэтому сначала копия.
    const messagesJson = [...this.value.messages]
      .reverse()
      .filter((e) => e.id !== messageId)
      .map(chatMessageToJson);
    const person = this.value.person;
    this.#completionConnection?.close();
    this.#completionConnection = SseConnection.open({
      url: person.completionUrl,
      body: {
        // `value.language.toString()` в Dart возвращал `param`.
        language_code: this.value.language,
        messages: messagesJson,
        stream: true,
        person_id: this.value.person.id,
        collection_name: this.value.person.collectionName,
      },
      onEvent: (event) => {
        this.#onCompletionEvent(event, messageId);
      },
      onFailure: (sseFailure) => {
        this.#onCompletionFailure(sseFailure, messageId);
      },
      onDone: () => {
        this.closeCurrentConnection();
        this.#endRecordCompleter();
      },
    });
  }

  /**
   * Тело `onEvent` потока ответа.
   *
   * Нераспознанное событие пропускается — так было и в Dart
   * (`case null: break;`). Но там это был молчаливый пропуск, и если бы
   * сервис сменил формат, экран просто остался бы пустым при работающем
   * потоке, без единого следа. Поэтому такое событие попадает в журнал
   * разработчика: поведение прежнее, причина видна.
   */
  #onCompletionEvent(event: SseEvent, messageId: number): void {
    const data = sseEventDataAsMap(event);
    if (data === null) {
      this.#logUnparsedEvent('данные события не объект', event);
      return;
    }
    const response = llmResponseModelTryFromJson(data);
    if (response === null) {
      this.#logUnparsedEvent('неизвестный тип события', event);
      return;
    }
    switch (response.type) {
      case 'context':
        this.updateValue((old) =>
          old.copyWith({ promptInfos: [...old.promptInfos, response.content] }),
        );
        break;
      case 'chunk':
        this.updateValue((old) => old.copyWith({ speeking: true }));
        this.editAssistantMessage(messageId, (old) =>
          assistantMessageCopyWith(old, {
            message: old.message + response.content.text,
          }),
        );
        break;
      case 'context_chunks':
        this.editAssistantMessage(messageId, (old) =>
          assistantMessageCopyWith(old, { chunks: response.data.chunks }),
        );
        break;
    }
  }

  /**
   * Событие потока, которое приложение не поняло.
   *
   * Блок без поля `data` пропускается молча: это keep-alive (`: ping`),
   * который Dart тоже отдавал в `onEvent` как событие с пустыми данными.
   * Писать в журнал каждый пульс соединения — значит утопить в нём всё
   * остальное. Сырой текст режется: весь ответ модели там не нужен, а для
   * опознания формата хватает начала.
   */
  #logUnparsedEvent(reason: string, event: SseEvent): void {
    if (!event.hasData) return;
    DiagnosticsLog.instance.stream({
      title: `completion: ${reason}`,
      details: event.raw.slice(0, UNPARSED_EVENT_LOG_LIMIT),
      isError: true,
    });
  }

  /** Тело `onFailure` потока ответа: код сбоя оседает на самом пузыре. */
  #onCompletionFailure(sseFailure: SseFailure, messageId: number): void {
    const failure = AppFailure.fromSse(FailureOp.completion, sseFailure);
    this.editAssistantMessage(messageId, (old) =>
      assistantMessageCopyWith(old, { errorMessage: new Nullable(failure.code) }),
    );
    this.closeCurrentConnection();
    SnackbarManager.instance.showFailure(failure, {
      onRetry: () => {
        this.#retryLastQuestion();
      },
    });
    this.#endRecordCompleter({ error: failure });
  }

  /**
   * Повторяет последний вопрос посетителя — по кнопке в тосте.
   *
   * Пузырь ответа, оставшийся от неудачной попытки, убирается, чтобы
   * в ленте не копились пустые.
   */
  #retryLastQuestion(): void {
    const lastQuestion = this.value.messages.find((e) => e.isMy) ?? null;
    if (lastQuestion === null) return;
    this.updateValue((old) =>
      old.copyWith({
        messages: old.messages.filter((e) => e.isMy || e.message.length > 0),
      }),
    );
    void this.#startFetchResponse();
  }

  /**
   * Подготовка записи — место `_getRecordDirectory()`.
   *
   * Временной папки в браузере нет, но флаг `preparingDirectory` остался:
   * на нём держится `preparingBeforeRecord`, по которому `_stopRecordAudio`
   * решает, можно ли отменить дубль целиком. Теперь он поднят на время
   * открытия микрофона — это и есть та самая «подготовка места», единственный
   * заметный по времени шаг перед записью (запрос устройства и AudioContext).
   */
  async #prepareRecorder(recorder: AudioRecorder): Promise<void> {
    try {
      this.updateValue((old) => old.copyWith({ preparingDirectory: true }));
      // WAV mono 16 кГц — то, что ждёт STT (Bifrost): сервер всё равно
      // приводит к этому формату, а файл и загрузка в ~11 раз меньше,
      // чем при 44.1 кГц стерео по умолчанию.
      //
      // Сам `RecordConfig(encoder: wav, sampleRate: 16000, numChannels: 1)`
      // и путь `<tmp>/<мс>_audio.wav` переехали внутрь AudioRecorder:
      // в браузере файловой системы нет, и параметры задавать негде.
      await recorder.start();
    } catch (e) {
      // Показ убран: метод бросает дальше, показывает внешний обработчик.
      // Раньше тост был и здесь, и в вызывающем — посетитель видел два подряд.
      throw AppFailure.device(FailureOp.record, e, stackTraceOf(e));
    } finally {
      this.updateValue((old) => old.copyWith({ preparingDirectory: false }));
    }
  }

  /** `_prepareRecordedFile()`: `stop()` отдаёт готовый WAV вместо пути. */
  async #prepareRecordedFile(): Promise<File> {
    try {
      this.updateValue((old) => old.copyWith({ preparingFile: true }));
      const file = await this.#requireRecorder().stop();
      if (file !== null) {
        return file;
      }
      throw new Error('Record not completed');
    } catch (e) {
      throw AppFailure.device(FailureOp.record, e, stackTraceOf(e));
    } finally {
      this.updateValue((old) =>
        old.copyWith({ isRecording: false, preparingFile: false }),
      );
    }
  }

  async #checkPermission(): Promise<boolean> {
    this.updateValue((old) => old.copyWith({ checkingAccess: true }));
    try {
      return await this.#requireRecorder().hasPermission();
    } catch (e) {
      throw AppFailure.device(FailureOp.permission, e, stackTraceOf(e));
    } finally {
      this.updateValue((old) => old.copyWith({ checkingAccess: false }));
    }
  }

  async #startRecordAudio(): Promise<void> {
    const oldCompleter = this.#recordCompleter;

    if (
      oldCompleter !== null &&
      !oldCompleter.isCompleted &&
      !oldCompleter.isCanceled
    ) {
      // Maybe show error
      return;
    }
    const recorder = new AudioRecorder();
    this.#recorder = recorder;
    const completer = new CancelableCompleter();
    this.#recordCompleter = completer;
    try {
      const hasPermission = await this.#checkPermission();

      if (hasPermission) {
        await this.#beginRecording(recorder, completer);
      } else {
        // `openAppSettings()` в вебе нет: страница не может открыть настройки
        // сайта, разрешение снимается только вручную — значком в адресной
        // строке. Показываем тот же текст, что каталог даёт на PERM-DEVICE
        // (`error_no_permission_access`), вместо перехода в настройки.
        SnackbarManager.instance.showFailure(
          new AppFailure({ op: FailureOp.permission, kind: FailureKind.device }),
        );
      }
    } catch (e) {
      this.#endRecordCompleter({ error: e });
      SnackbarManager.instance.showFailure(
        AppFailure.fromException(FailureOp.record, e),
      );
    }
  }

  /** Вторая половина `_startRecordAudio`: подготовка и переход в запись. */
  async #beginRecording(
    recorder: AudioRecorder,
    completer: CancelableCompleter,
  ): Promise<void> {
    await this.#prepareRecorder(recorder);
    if (completer.isCanceled) {
      // В Dart на этой ветке запись ещё не начиналась — отменять было нечего.
      // Здесь микрофон уже открыт, поэтому его надо отпустить, иначе индикатор
      // записи во вкладке останется гореть до перезагрузки киоска.
      recorder.dispose();
      return;
    }
    this.updateValue((old) => old.copyWith({ isRecording: true }));
  }

  async #stopRecordAudio(): Promise<void> {
    try {
      const completer = this.#recordCompleter;
      if (completer === null) {
        return;
      }
      const isCancelAvailable = this.value.preparingBeforeRecord;
      if (!completer.isCompleted && isCancelAvailable) {
        completer.operation.cancel();
        return;
      }
      const file = await this.#prepareRecordedFile();
      const transcription = await this.getTranscription(file);
      if (transcription.trim().length > 0) {
        const message = createMyMessage({ audio: file, message: transcription });
        this.addMessage(message);
        void this.#startFetchResponse();
      } else {
        this.#endRecordCompleter();
        SnackbarManager.instance.showError({
          description: this.l10n?.does_not_recognized,
        });
      }
    } catch (e) {
      this.#endRecordCompleter();
      // Единственная точка показа для всей цепочки записи: подготовка места,
      // разрешение, сохранение файла и транскрипция бросают сюда.
      SnackbarManager.instance.showFailure(
        AppFailure.fromException(FailureOp.transcribe, e, stackTraceOf(e)),
        {
          onRetry: () => {
            this.#retryRecordHint();
          },
        },
      );
    }
  }

  #endRecordCompleter({ error }: EndRecordCompleterOptions = {}): void {
    const completer = this.#recordCompleter;
    if (completer !== null && !completer.isCompleted) {
      if (error !== undefined && error !== null) {
        completer.completeError(error);
      } else {
        completer.complete();
      }
    }
    this.updateValue((old) => old.copyWith({ isRecording: false }));
  }

  /**
   * Кнопка «Повторить» после неудачной расшифровки: записать нечего заново
   * без участия посетителя, поэтому просто снимаем состояние занятости,
   * чтобы он мог нажать микрофон сам.
   */
  #retryRecordHint(): void {
    this.#endRecordCompleter();
  }

  /** `_recorder!` — в Dart тут был null-assert, падавший на пустом поле. */
  #requireRecorder(): AudioRecorder {
    const recorder = this.#recorder;
    if (recorder === null) {
      throw new Error('ChatController: запись не начата');
    }
    return recorder;
  }
}
